import type { CSSProperties } from "react"
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react"
import {
	IconPlusOutline18 as PlusIcon,
	IconTableRowNewBottomOutline18 as RowIcon,
	IconXmarkOutline18 as XmarkIcon,
} from "nucleo-ui-outline-18"

import {
	ColorItemExtension,
	sanitizeColorValue,
} from "@productlane/lib/src/tiptap/extensions/mintlify/color"
import { IconButton, Tooltip } from "@productlane/ui"

import type { NodeViewProps } from "@tiptap/react"

function ColorItemNodeView({ node, editor, getPos }: NodeViewProps) {
	const name = String(node.attrs.name ?? "")
	const value = String(node.attrs.value ?? "")
	const dark = node.attrs.dark ? String(node.attrs.dark) : ""
	const light = sanitizeColorValue(value)
	const style = {
		"--swatch-light": light || "transparent",
		"--swatch-dark": sanitizeColorValue(dark) || light || "transparent",
	} as CSSProperties

	const pos = () => {
		const at = getPos()
		return typeof at === "number" ? at : undefined
	}
	const update = (
		attrs: Partial<{ name: string; value: string; dark: string | null }>,
	) => {
		const at = pos()
		if (at !== undefined) editor.commands.updateColorItem(at, attrs)
	}
	const inRow = (() => {
		const at = pos()
		return (
			at !== undefined &&
			editor.state.doc.resolve(at).parent.type.name === "colorRow"
		)
	})()

	return (
		<NodeViewWrapper
			data-type="color-item"
			className="pl-color-item"
			style={style}
			contentEditable={false}
		>
			<div className="pl-color-swatch" aria-hidden="true" />
			{editor.isEditable ? (
				<div className="pl-color-editor">
					<input
						className="pl-color-editor-name"
						value={name}
						placeholder="Name"
						aria-label="Color name"
						onChange={(event) => update({ name: event.target.value })}
					/>
					<input
						value={value}
						placeholder="#3B82F6"
						aria-label="Color value"
						spellCheck={false}
						onChange={(event) => update({ value: event.target.value })}
					/>
					<input
						value={dark}
						placeholder="Dark value"
						aria-label="Dark mode value"
						spellCheck={false}
						onChange={(event) => update({ dark: event.target.value || null })}
					/>
					<div className="pl-color-editor-actions">
						<Tooltip label="Add color">
							<IconButton
								variant="ghost"
								size="sm"
								Icon={PlusIcon}
								aria-label="Add color"
								className="h-4 w-4 flex-none cursor-default"
								onClick={() => {
									const at = pos()
									if (at !== undefined) editor.commands.addColorItem(at)
								}}
							/>
						</Tooltip>
						{inRow && (
							<Tooltip label="Add row">
								<IconButton
									variant="ghost"
									size="sm"
									Icon={RowIcon}
									aria-label="Add row"
									className="h-4 w-4 flex-none cursor-default"
									onClick={() => {
										const at = pos()
										if (at !== undefined) editor.commands.addColorRow(at)
									}}
								/>
							</Tooltip>
						)}
						<Tooltip label="Remove color">
							<IconButton
								variant="ghost"
								size="sm"
								Icon={XmarkIcon}
								aria-label="Remove color"
								className="h-4 w-4 flex-none cursor-default"
								onClick={() => {
									const at = pos()
									if (at !== undefined) editor.commands.removeColorItem(at)
								}}
							/>
						</Tooltip>
					</div>
				</div>
			) : (
				<div className="pl-color-meta">
					<p className="pl-color-name">{name}</p>
					<p className="pl-color-value">{value}</p>
				</div>
			)}
		</NodeViewWrapper>
	)
}

export const ColorItemWithNodeView = ColorItemExtension.extend({
	addNodeView() {
		return ReactNodeViewRenderer(ColorItemNodeView)
	},
})
